import { useEffect, useState } from "react";
import { cn } from "@/utils/string";
import { useMe } from "@/data/user";
import { useModalAction } from "@/contexts/modal/Context";
import { Trans, useTranslation } from "react-i18next";
import { maixpanelTrackEventProps } from "@/mixpanel/MixpanelUtils";
import { AvailableDaysItem, LoginUserItem } from "@/types";
import dayjs, { Dayjs } from "dayjs";
import Button from "@edusogno/primitives/button.js";
import Warning from "@/components/ui/Warning";
import MobileLayout from "@/components/one-to-one/MobileLayout";
import CustomDatePicker from "@edusogno/composites/calendar";

interface MobileViewProps {
  date: Dayjs;
  setDate: (date: Dayjs) => void;
  hoursWriting: number;
  availableDays: Dayjs[];
  countCorrections: number;
  availableDaysArray: AvailableDaysItem[];
  countBookedCorrections: number;
}

const BookLiveCorrectionMobileView = ({
  date,
  setDate,
  hoursWriting,
  availableDays,
  availableDaysArray,
  countBookedCorrections,
}: MobileViewProps) => {
  const { me } = useMe();
  const { t, i18n } = useTranslation();
  const { openModal } = useModalAction();

  const [hour, setHour] = useState<string | null>(null);
  const [steps, setSteps] = useState(1);
  const [selectedTutor, setSelectedTutor] = useState<string>();

  const mixpanelPropsData = JSON.stringify(
    maixpanelTrackEventProps("Exercise", me?.item?.user as LoginUserItem),
  );

  useEffect(() => {
    try {
      const defaultDate = availableDays[0].format("MM/DD/YY");
      setDate(dayjs(defaultDate));
    } catch (error) {
      console.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDays.length]);

  const StepOne = () => {
    return (
      <div className="w-full gap-x-4 flex flex-col h-full justify-between">
        <CustomDatePicker
          type="single"
          lang={i18n.language as "en" | "it" | "es" | "fr" | "de"}
          value={date.format("YYYY-MM-DD HH:mm:ss")}
          minDate={dayjs()}
          className="w-full"
          openDates={availableDays}
          onValueChange={(value) => {
            if (value) {
              setDate(dayjs(value as string));
            }
          }}
        />

        <div className="flex w-full flex-col gap-2">
          <Warning
            text={
              <Trans
                i18nKey="one_to_one.warning_timezome"
                components={{
                  b: <b className="font-semibold" />,
                }}
              ></Trans>
            }
          />

          <Button
            size="md"
            color="primary"
            onClick={() => setSteps(2)}
            disabled={!date}
            className="mx-auto"
          >
            {t("btns.next")}
          </Button>
        </div>
      </div>
    );
  };

  const StepTwo = () => {
    return (
      <div className="w-full flex flex-col h-full justify-between gap-x-4">
        <div className="grid grid-cols-2 gap-2">
          {availableDaysArray
            ?.map((cal) => dayjs(cal.date).format("HH:mm"))
            .map((time) => (
              <Button
                onClick={() => {
                  if (time) {
                    setHour(time);

                    const tutor = availableDaysArray.find(
                      (el) =>
                        el.date === `${date.format("YYYY-MM-DD")} ${time}`,
                    );

                    if (tutor) {
                      setSelectedTutor(tutor.tutor);
                    }
                  }
                }}
                className={cn`${
                  hour == time
                    ? "bg-brand-deep text-white"
                    : "bg-[#EEE] text-secondary"
                }border-0`}
              >
                {time}
              </Button>
            ))}
        </div>

        <div className="flex w-full flex-col gap-2">
          <Warning
            text={
              <Trans
                i18nKey="one_to_one.warning_timezome"
                components={{
                  b: <b className="font-semibold" />,
                }}
              ></Trans>
            }
          />
          <Button
            data-track-props={mixpanelPropsData}
            data-track-eventtype="Excersie_Confirm_Live_Correction"
            disabled={!hour}
            onClick={() => {
              if (
                countBookedCorrections &&
                hoursWriting &&
                countBookedCorrections > hoursWriting
              ) {
                openModal("LIVE_CORRECTION_BOOKING_ERROR_MODAL", {
                  bookingError: true,
                  countBookedCorrections,
                  hoursWriting,
                });
              } else {
                openModal(
                  "CONFIRM_LIVE_CORRECTION_BOOKING_MODAL",
                  {
                    date,
                    hour,
                    tutor: selectedTutor,
                  },
                  {
                    contentClass: "bg-white bg-fit",
                    renderHeader: (
                      <div className="flex w-full justify-center md:justify-start items-center px-2 h-10 bg-brand-secondary rounded-t-md">
                        <h2 className="text-sm font-semibold text-center md:text-left text-light">
                          Book live correction
                        </h2>
                      </div>
                    ),
                  },
                );
              }
            }}
            size="md"
            className="mx-auto bg-socials-whatsapp"
          >
            {t("btns.confirm")}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <MobileLayout
      title={t("book_live_correction.mobile.header")}
      subtitle={t("book_live_correction.mobile.sub_header")}
      isOneToOne={false}
    >
      {steps == 1 && <StepOne />}
      {steps == 2 && <StepTwo />}
    </MobileLayout>
    //   <div className={"h-full w-full pt-4"}>
    //     <div className={"space-y-2 w-full mb-4 lg:mb-6"}>
    //       <p className="text-light text-4xl font-semibold">
    //         {/* Writing correction */}
    //         {t("book_live_correction.mobile.header")}
    //       </p>
    //       <p className={"text-xl text-light"}>
    //         {t("book_live_correction.mobile.sub_header")}
    //         {/* Choose date & time of the live correction */}
    //       </p>
    //     </div>
    //     <div className={"w-full h-full"}>
    //       <div
    //         className={"flex w-full flex-col rounded-t-lg bg-white py-4 gap-8"}
    //       >
    //         <div className={"w-full gap-x-4 h-full"}>
    //           <CustomDatePicker
    //             lang={i18n.language as "en" | "it" | "es" | "fr" | "de"}
    //             openDates={availableDays}
    //             onValueChange={(value) => {
    //               if (value) {
    //                 setDate(dayjs(value as string));
    //               }
    //             }}
    //             minDate={dayjs()}
    //             value={date.format("YYYY-MM-DD HH:mm:ss")}
    //             type={"single"}
    //             className={"w-full"}
    //           />
    //       </div>

    //     </div>
    //   </div>
    // </div>
  );
};

export default BookLiveCorrectionMobileView;
